import styled from "styled-components";
import { Link } from "react-router-dom";
import { CmsSection } from "@interfaces";
import { breakpoint, theme } from "@utilities";

interface IProps {
    sections: CmsSection[];
}

const isExternal = (href?: string) => !!href && /^https?:\/\//i.test(href);

const CtaLink = ({ href, label, external }: { href?: string; label?: string; external?: boolean }) => {
    if (!href || !label) return null;
    if (external || isExternal(href)) {
        return (
            <a className="cmsCta" href={href} target="_blank" rel="noreferrer">
                {label}
            </a>
        );
    }
    return (
        <Link className="cmsCta" to={href}>
            {label}
        </Link>
    );
};

/**
 * Renders reusable CMS section blocks. New page layouts are composed from these
 * data-driven blocks (no code deploy needed to arrange them). Unknown block
 * types are skipped gracefully. Rich text uses `dangerouslySetInnerHTML`; the
 * backend is responsible for sanitising editor HTML before storing it.
 */
export const SectionBlockRenderer = ({ sections }: IProps) => {
    if (!sections || !sections.length) return null;
    return (
        <>
            {sections.map((section) => {
                const { id, type, data } = section;
                switch (type) {
                    case "hero":
                        return (
                            <Hero key={id} $align={data.align} $bg={data.backgroundColor}>
                                {data.imageUrl && (
                                    <img className="heroImg" src={data.imageUrl} alt={data.imageAlt ?? ""} />
                                )}
                                <div className="heroText">
                                    {data.heading && <h1 className="heroHeading">{data.heading}</h1>}
                                    {data.subheading && <p className="heroSub">{data.subheading}</p>}
                                    <CtaLink href={data.ctaHref} label={data.ctaLabel} external={data.ctaExternal} />
                                </div>
                            </Hero>
                        );
                    case "richText":
                        return (
                            <RichText key={id} $align={data.align}>
                                {data.heading && <h2>{data.heading}</h2>}
                                {data.body && <div dangerouslySetInnerHTML={{ __html: data.body }} />}
                            </RichText>
                        );
                    case "imageText":
                        return (
                            <ImageText key={id} $position={data.imagePosition}>
                                {data.imageUrl && (
                                    <img src={data.imageUrl} alt={data.imageAlt ?? ""} className="itImg" />
                                )}
                                <div className="itBody">
                                    {data.heading && <h2>{data.heading}</h2>}
                                    {data.body && <div dangerouslySetInnerHTML={{ __html: data.body }} />}
                                    <CtaLink href={data.ctaHref} label={data.ctaLabel} external={data.ctaExternal} />
                                </div>
                            </ImageText>
                        );
                    case "gallery":
                        return (
                            <Gallery key={id}>
                                {data.heading && <h2 className="secHeading">{data.heading}</h2>}
                                <div className="grid">
                                    {(data.items ?? []).map((item, i) => (
                                        <figure key={item.id ?? i}>
                                            {item.imageUrl && (
                                                <img src={item.imageUrl} alt={item.imageAlt ?? item.title ?? ""} />
                                            )}
                                            {item.title && <figcaption>{item.title}</figcaption>}
                                        </figure>
                                    ))}
                                </div>
                            </Gallery>
                        );
                    case "cardGrid":
                        return (
                            <CardGrid key={id}>
                                {data.heading && <h2 className="secHeading">{data.heading}</h2>}
                                <div className="grid">
                                    {(data.items ?? []).map((item, i) => {
                                        const inner = (
                                            <>
                                                {item.imageUrl && (
                                                    <img src={item.imageUrl} alt={item.imageAlt ?? item.title ?? ""} />
                                                )}
                                                {item.title && <h3>{item.title}</h3>}
                                                {item.body && <p>{item.body}</p>}
                                            </>
                                        );
                                        return item.href ? (
                                            <Link className="card" key={item.id ?? i} to={item.href}>
                                                {inner}
                                            </Link>
                                        ) : (
                                            <div className="card" key={item.id ?? i}>
                                                {inner}
                                            </div>
                                        );
                                    })}
                                </div>
                            </CardGrid>
                        );
                    case "faq":
                        return (
                            <Faq key={id}>
                                {data.heading && <h2 className="secHeading">{data.heading}</h2>}
                                {(data.items ?? []).map((item, i) => (
                                    <details key={item.id ?? i}>
                                        <summary>{item.question ?? item.title}</summary>
                                        {(item.answer ?? item.body) && (
                                            <div
                                                dangerouslySetInnerHTML={{
                                                    __html: item.answer ?? item.body ?? "",
                                                }}
                                            />
                                        )}
                                    </details>
                                ))}
                            </Faq>
                        );
                    case "cta":
                        return (
                            <Cta key={id} $bg={data.backgroundColor}>
                                {data.heading && <h2>{data.heading}</h2>}
                                {data.subheading && <p>{data.subheading}</p>}
                                <CtaLink href={data.ctaHref} label={data.ctaLabel} external={data.ctaExternal} />
                            </Cta>
                        );
                    case "html":
                        return data.html ? (
                            <RawHtml key={id} dangerouslySetInnerHTML={{ __html: data.html }} />
                        ) : null;
                    case "spacer":
                        return <div key={id} style={{ height: data.height ?? 32 }} />;
                    default:
                        return null;
                }
            })}
        </>
    );
};

const wide = `
    width: 100%;
    max-width: 1110px;
    margin-left: auto;
    margin-right: auto;
    padding: 0 16px;
`;

const headingFont = theme.fonts?.primary ?? "'Poppins', sans-serif";
const brandYellow = theme.colors?.main ?? "#FFD200";
const brandCtaText = theme.colors?.button_text ?? "#000000";
const brandBlue = theme.colors?.highlight ?? "#2E5AA9";
const brandBlack = theme.colors?.black ?? "#0D0D0D";
const radiusMd = theme.borderRadius?.medium ?? "8px";
const radiusLg = theme.borderRadius?.large ?? "16px";

// Signature Peterpans hero: a bold, full-bleed dark section with a large
// Poppins heading overlaid on the image and a yellow call-to-action.
const Hero = styled.section<{ $align?: string; $bg?: string }>`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: ${(p) =>
        p.$align === "center" ? "center" : p.$align === "right" ? "flex-end" : "flex-start"};
    min-height: 340px;
    background: ${(p) => p.$bg ?? brandBlack};
    text-align: ${(p) => p.$align ?? "center"};
    overflow: hidden;
    margin-bottom: 40px;
    ${breakpoint.breakTablet`min-height: 460px;`}

    .heroImg {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
        z-index: 0;
    }
    &::after {
        content: "";
        position: absolute;
        inset: 0;
        background: linear-gradient(180deg, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0.6) 100%);
        z-index: 1;
    }
    .heroText {
        position: relative;
        z-index: 2;
        max-width: 900px;
        padding: 48px 24px;
    }
    .heroHeading {
        font-family: ${headingFont};
        font-size: 34px;
        font-weight: 700;
        line-height: 1.1;
        color: #fff;
        margin: 0 0 12px;
        ${breakpoint.breakTablet`font-size: 52px;`}
    }
    .heroSub {
        font-size: 17px;
        line-height: 1.5;
        color: rgba(255, 255, 255, 0.9);
        margin: 0;
    }
    .cmsCta {
        display: inline-block;
        margin-top: 24px;
        padding: 14px 32px;
        border-radius: ${radiusMd};
        background: ${brandYellow};
        color: ${brandCtaText};
        font-family: ${headingFont};
        font-weight: 700;
        text-decoration: none;
        transition: transform 0.15s ease, filter 0.15s ease;
    }
    .cmsCta:hover {
        filter: brightness(0.94);
        transform: translateY(-1px);
    }
`;

const RichText = styled.section<{ $align?: string }>`
    ${wide}
    text-align: ${(p) => p.$align ?? "left"};
    margin-bottom: 40px;
    line-height: 1.7;
    color: ${theme.colors?.gray_1 ?? "#1a1a1a"};

    h2 {
        font-family: ${headingFont};
        font-size: 30px;
        font-weight: 700;
        color: ${brandBlack};
        margin-bottom: 16px;
    }
    p {
        margin-bottom: 12px;
    }
    a {
        color: ${brandBlue};
        font-weight: 600;
    }
`;

const ImageText = styled.section<{ $position?: string }>`
    ${wide}
    display: flex;
    flex-direction: ${(p) => (p.$position === "right" ? "row-reverse" : "row")};
    gap: 40px;
    align-items: center;
    margin-bottom: 48px;

    .itImg {
        width: 48%;
        border-radius: ${radiusLg};
        object-fit: cover;
    }
    .itBody {
        flex: 1;
        line-height: 1.7;
        color: ${theme.colors?.gray_1 ?? "#1a1a1a"};
    }
    .itBody h2 {
        font-family: ${headingFont};
        font-size: 30px;
        font-weight: 700;
        color: ${brandBlack};
        margin-bottom: 12px;
    }
    .cmsCta {
        display: inline-block;
        margin-top: 16px;
        padding: 12px 28px;
        border-radius: ${radiusMd};
        background: ${brandYellow};
        color: ${brandCtaText};
        font-family: ${headingFont};
        font-weight: 700;
        text-decoration: none;
    }
    ${breakpoint.breakOnlyMobile`
        flex-direction: column;
        gap: 24px;
        .itImg { width: 100%; }
    `}
`;

const sectionHeading = `
    .secHeading {
        font-family: ${headingFont};
        font-size: 30px;
        font-weight: 700;
        color: ${brandBlack};
        margin-bottom: 20px;
    }
`;

const Gallery = styled.section`
    ${wide}
    ${sectionHeading}
    margin-bottom: 32px;

    .grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
        gap: 16px;
    }
    figure {
        margin: 0;
        overflow: hidden;
        border-radius: ${radiusLg};
    }
    figure img {
        width: 100%;
        aspect-ratio: 4 / 3;
        object-fit: cover;
        display: block;
        transition: transform 0.3s ease;
    }
    figure:hover img {
        transform: scale(1.05);
    }
    figcaption {
        font-size: 14px;
        color: ${theme.colors?.gray_2 ?? "#555"};
        margin-top: 8px;
    }
`;

const CardGrid = styled.section`
    ${wide}
    ${sectionHeading}
    margin-bottom: 32px;

    .grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
        gap: 24px;
    }
    .card {
        display: block;
        border-radius: ${radiusLg};
        overflow: hidden;
        text-decoration: none;
        color: inherit;
        background: #fff;
        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
        transition: transform 0.2s ease, box-shadow 0.2s ease;
    }
    a.card:hover {
        transform: translateY(-4px);
        box-shadow: 0 14px 32px rgba(0, 0, 0, 0.16);
    }
    .card img {
        width: 100%;
        aspect-ratio: 3 / 2;
        object-fit: cover;
        display: block;
        margin-bottom: 0;
    }
    .card h3 {
        font-family: ${headingFont};
        font-size: 19px;
        font-weight: 700;
        color: ${brandBlack};
        margin: 16px 16px 6px;
    }
    .card p {
        font-size: 14px;
        line-height: 1.5;
        color: ${theme.colors?.gray_2 ?? "#555"};
        margin: 0 16px 18px;
    }
`;

const Faq = styled.section`
    ${wide}
    ${sectionHeading}
    margin-bottom: 32px;

    details {
        border-bottom: 1px solid ${theme.colors?.gray_5 ?? "#e0e0e0"};
        padding: 16px 0;
    }
    summary {
        cursor: pointer;
        font-family: ${headingFont};
        font-weight: 600;
        font-size: 17px;
        color: ${brandBlack};
        list-style: none;
    }
    summary::-webkit-details-marker {
        display: none;
    }
    summary::after {
        content: "+";
        float: right;
        color: ${brandBlue};
        font-weight: 700;
    }
    details[open] summary::after {
        content: "\\2013";
    }
    details > div {
        margin-top: 10px;
        line-height: 1.6;
        color: ${theme.colors?.gray_2 ?? "#555"};
    }
`;

const Cta = styled.section<{ $bg?: string }>`
    ${wide}
    text-align: center;
    background: ${(p) => p.$bg ?? brandYellow};
    border-radius: ${radiusLg};
    padding: 56px 24px;
    margin-bottom: 40px;

    h2 {
        font-family: ${headingFont};
        font-size: 30px;
        font-weight: 700;
        color: ${brandBlack};
        margin-bottom: 10px;
    }
    p {
        color: ${theme.colors?.gray_1 ?? "#1a1a1a"};
        margin-bottom: 24px;
    }
    .cmsCta {
        display: inline-block;
        padding: 14px 34px;
        border-radius: ${radiusMd};
        background: ${brandBlack};
        color: #fff;
        font-family: ${headingFont};
        font-weight: 700;
        text-decoration: none;
        transition: filter 0.15s ease, transform 0.15s ease;
    }
    .cmsCta:hover {
        filter: brightness(1.15);
        transform: translateY(-1px);
    }
`;

const RawHtml = styled.section`
    ${wide}
    margin-bottom: 32px;
`;
